import type { FC } from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { MiniFlexTable, Button } from "@similarweb/ui-components";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { ReferringCategoryCell } from "app/components/React/Table/cells";
import { TrafficShare } from "app/components/React/Table/cells/TrafficShare";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { CenteredFlexRow, FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { useCurrentStateAndParams } from "@uirouter/react";

const ContentWrapper = styled(CenteredFlexRow)`
    height: 284px;
`;

const HeaderStyle = styled(FlexRow)`
    justify-content: space-between;
    box-sizing: border-box;
    padding: 24px;
`;

const CtaWrapper = styled(RightFlexRow)`
    height: 52px;
    border-top: 1px solid ${colorsPalettes.carbon[100]};

    button {
        margin: 5px 8px;
    }
`;

const TopAdDestinationWrapper = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    height: 384px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 4px;
`;

const CtaButton = styled(Button)`
    margin: 5px 8px 5px 8px;
`;

const StyledTable: any = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
        height: 232px;
        flex: 1;
    }

    .MiniFlexTable-cell {
        height: 40px;
        vertical-align: middle;
        font-size: 14px;
        &:not(:first-child) {
            border-top: 1px solid ${colorsPalettes.carbon[50]};
        }
    }

    .MiniFlexTable-column {
        &:first-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-left: 24px;
            }
        }

        &:last-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-right: 24px;
            }
        }

        .MiniFlexTable-cell {
            > div {
                height: 100%;
                align-items: center;
                display: flex;
            }
        }
    }
    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

interface IWWOTopReferringIndustriesTableProps {
    unlockHook?: any;
    getCtaLink: (innerLevelPackage?: string) => { href: string; target: string };
}

export const WWOTopReferringIndustriesTable: FC<IWWOTopReferringIndustriesTableProps> = ({
    getCtaLink,
    unlockHook = null,
}) => {
    const { params, state } = useCurrentStateAndParams();
    const { country, key, isWWW } = params;
    let { duration } = params;
    const { totalApiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);
    if (isWWWithoutPermissions) {
        duration = BasicDurations.LAST_THREE_MONTHS;
    }
    const configId = proNavigator.getConfigId(state);
    const durationObject = DurationService.getDurationData(duration, undefined, configId);
    const { from, to, isWindow } = durationObject.forAPI;
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);
    const i18n = i18nFilter();
    const title = i18n("wa.wwo.topreferringcategories.title");
    const tooltip = i18n("wa.wwo.topreferringcategories.title.tooltip");
    const [data, setData] = useState<object[]>(null);
    const [columns, setColumns] = useState<object[]>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const is28d = duration === "28d";
    const { selectorHandlers } = useSelectorTrigger("wwo-top-referring-industries");
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country)?.text,
        },
        {
            filter: "webSource",
            value: "Total",
        },
    ];
    const ctaLink = getCtaLink();

    const transformData = (data) => {
        return data.Data.filter((item) => item.Category !== "Unknown")
            .slice(0, 5)
            .map((record) => {
                const mainCategory = record?.Category.substring(0, record.Category.indexOf("/"));
                const category = record?.Category.substring(record.Category.indexOf("/") + 1);

                return {
                    ...record,
                    url: proNavigator.href("digitalsuite_website_websiteperformance", {
                        ...params,
                        key: record.Domain,
                    }),
                    category: category === mainCategory ? category + `  - Other` : category,
                    categoryForLink: record?.Category.replace("/", "~"),
                };
            });
    };

    useEffect(() => {
        async function fetchData() {
            setData(null);

            const response = await DefaultFetchService.getInstance()
                .get<{ Data: object[] }>(`widgetApi/${totalApiController}/TopReferringCategories/Table`, {
                    country,
                    from,
                    includeSubDomains: isWWW === "*",
                    isWindow,
                    keys: key,
                    timeGranularity: is28d ? "Daily" : "Monthly",
                    to,
                    webSource: devicesTypes.TOTAL,
                    orderBy: "TotalShare desc",
                })
                .finally(() => {
                    setIsLoading(false);
                });
            setData(transformData(response));
            setColumns(getTableColumns());
        }
        fetchData();
    }, [from, to, isWindow, country, key]);

    const getTableColumns = () => {
        return [
            {
                field: "category",
                displayName: i18n("wa.wwo.topreferringindustries.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: (props) => (
                    <ReferringCategoryCell
                        {...props}
                        isIconHidden
                        getLink={() =>
                            proNavigator.href("digitalsuite_website_referrals_incomingtraffic", {
                                ...params,
                                referralsCategory: props.row.categoryForLink,
                            })
                        }
                    />
                ),
            },
            {
                field: "Share",
                displayName: i18n("wa.wwo.topreferringindustries.share"),
                headerComponent: DefaultCellHeader,
                cellComponent: TrafficShare,
            },
        ];
    };

    const ctaClicked = () => {
        allTrackers.trackEvent(
            "Internal Link",
            "click",
            "Marketing Mix/Top Referring Categories/See more referring industries",
        );
    };

    const getComponent = () =>
        data && data.length > 0 && columns ? (
            <>
                <StyledTable
                    className="MiniFlexTable"
                    data={data}
                    columns={columns}
                />
                <CtaWrapper>
                    <FooterComponent
                        href={ctaLink.href}
                        onClick={ctaClicked}
                        text="wwo.referring.categories.button"
                        unlockHook={unlockHook}
                        target={ctaLink.target}
                    />
                </CtaWrapper>
            </>
        ) : (
            <ContentWrapper>
                <EmptyState
                    titleKey="wa.wwo.no.data.title"
                    subTitleKey="wa.wwo.no.data.sub.title"
                />
            </ContentWrapper>
        );

    return (
        <TopAdDestinationWrapper data-automation="wwo-top-referring-industries">
            <HeaderStyle>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                    {...selectorHandlers}
                />
            </HeaderStyle>
            {isLoading ? (
                <ContentWrapper>
                    <WidgetLoader />
                </ContentWrapper>
            ) : (
                getComponent()
            )}
        </TopAdDestinationWrapper>
    );
};
